import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Badge } from 'react-bootstrap';
import { FaPaperPlane, FaPaperclip, FaFileAlt, FaTimes, FaArrowLeft, FaInfoCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ChatService from '../../services/chat.service';
import API from '../../services/api';
import './StudentChat.css';

const StudentChat = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [attachment, setAttachment] = useState(null);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (user?.id) {
      loadChatHistory();
      const interval = setInterval(loadChatHistory, 5000); // Polling history every 5s
      return () => clearInterval(interval);
    }
  }, [user]);

  // Auto-scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  const loadChatHistory = async () => {
    try {
      const res = await ChatService.getStudentChats(user.id);
      setMessages(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageInput.trim() && !attachment) return;

    const formData = new FormData();
    formData.append('sender_type', 'student');
    formData.append('sender_id', user.id);
    formData.append('receiver_type', 'admin');
    formData.append('receiver_id', 0);
    if (messageInput.trim()) formData.append('message', messageInput.trim());
    if (attachment) formData.append('attachment', attachment);

    setMessageInput('');
    setAttachment(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    try {
      await ChatService.sendMessage(formData);
      loadChatHistory();
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const renderAttachment = (url) => {
    if (!url) return null;
    const API_URL = API.defaults.baseURL;
    const fullUrl = url.startsWith('http') ? url : `${API_URL.replace('/api', '')}/${url}`;
    const ext = url.split('.').pop().toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);

    if (isImage) {
      return (
        <div className="chat-attachment image">
          <a href={fullUrl} target="_blank" rel="noopener noreferrer">
            <img src={fullUrl} alt="attachment" />
          </a>
        </div>
      );
    }
    return (
      <div className="chat-attachment file">
        <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center text-decoration-none">
          <FaFileAlt className="me-2" /> View Attachment
        </a>
      </div>
    );
  };

  return (
    <div className="student-chat-page d-flex flex-column">
      <div className="student-chat-header p-3 bg-white border-bottom d-flex align-items-center sticky-top shadow-sm z-3">
        <Button variant="link" className="text-dark p-0 me-3" onClick={() => navigate(-1)}>
          <FaArrowLeft size={20} />
        </Button>
        <div className="d-flex align-items-center">
          <div className="chat-avatar me-3 bg-primary text-white d-flex align-items-center justify-content-center rounded-circle" style={{ width: '40px', height: '40px' }}>
            A
          </div>
          <div>
            <h6 className="mb-0 fw-bold">Admin Support</h6>
            <small className="text-success">Online</small>
          </div>
        </div>
      </div>

      <div className="student-chat-messages flex-grow-1 p-3 overflow-auto" style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/cubes.png")' }}>
        {messages.length === 0 ? (
          <div className="d-flex h-100 align-items-center justify-content-center flex-column text-muted mt-5 pt-5">
            <div className="p-4 bg-white rounded-circle shadow-sm mb-3">
              <FaPaperPlane size={40} className="text-primary opacity-50" />
            </div>
            <p>Welcome to Chat Support. How can we help you today?</p>
          </div>
        ) : (
          messages.map((msg, index) => {
            const isMe = msg.sender_type === 'student' && msg.sender_id === user.id;
            const isBroadcast = msg.receiver_type === 'batch' || msg.receiver_type === 'course';
            
            return (
              <div key={msg.id || index} className={`message-wrapper d-flex mb-3 ${isMe ? 'justify-content-end' : 'justify-content-start'}`}>
                <div className={`message-bubble p-3 shadow-sm ${isMe ? 'bg-primary text-white rounded-start rounded-bottom' : 'bg-white text-dark rounded-end rounded-bottom'}`} style={{ maxWidth: '85%' }}>
                  {!isMe && isBroadcast && (
                    <div className="mb-2 text-primary" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                      <FaInfoCircle className="me-1" /> {msg.receiver_type === 'batch' ? 'Batch Announcement' : 'Course Announcement'}
                    </div>
                  )}
                  {msg.attachment_url && renderAttachment(msg.attachment_url)}
                  {msg.message && <div className="message-text" style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</div>}
                  <div className={`message-time text-end mt-1 ${isMe ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: '0.7rem' }}>
                    {formatTime(msg.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="student-chat-input-area">
        {attachment && (
          <div className="mb-2 p-2 bg-light rounded d-flex align-items-center justify-content-between border">
            <div className="text-truncate me-3">
              <FaPaperclip className="me-2" /> {attachment.name}
            </div>
            <Button variant="link" className="text-danger p-0" onClick={() => { setAttachment(null); fileInputRef.current.value = ''; }}>
              <FaTimes />
            </Button>
          </div>
        )}
        <Form onSubmit={handleSendMessage} className="d-flex align-items-end">
          <Button 
            variant="light" 
            className="rounded-circle text-muted p-2 me-2 shadow-none" 
            onClick={() => fileInputRef.current?.click()}
          >
            <FaPaperclip size={20} />
          </Button>
          <input 
            type="file" 
            ref={fileInputRef} 
            className="d-none" 
            onChange={(e) => setAttachment(e.target.files[0])} 
          />
          <Form.Control
            as="textarea"
            ref={textareaRef}
            rows={1}
            placeholder="Type a message..."
            className="border-0 shadow-none bg-light rounded-pill px-4 py-2 resize-none"
            value={messageInput}
            onChange={(e) => {
              setMessageInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e);
                e.target.style.height = 'auto'; // Reset height after send
              }
            }}
            style={{ resize: 'none', maxHeight: '120px', overflowY: 'auto' }}
          />
          <Button 
            type="submit" 
            variant="primary" 
            className="rounded-circle ms-2 d-flex align-items-center justify-content-center shadow-sm"
            style={{ width: '45px', height: '45px', flexShrink: 0 }}
            disabled={!messageInput.trim() && !attachment}
          >
            <FaPaperPlane />
          </Button>
        </Form>
      </div>
    </div>
  );
};

export default StudentChat;
